import React from 'react';
import './Pagination.scss';

interface PaginationComponentProps {
  resultCount: number; // total items
  pageSize: number; // items per page
  pageIndex: number; // current page (zero-based)
  pageSizeOptions?: number[];
  onPageChange: (newPageIndex: number) => void;
  onPageSizeChange?: (newSize: number) => void;
  showFirstLastButtons?: boolean;
}

const PaginationComponent: React.FC<PaginationComponentProps> = ({
  resultCount,
  pageSize,
  pageIndex,
  pageSizeOptions = [5, 10, 25, 50],
  onPageChange,
  onPageSizeChange,
  showFirstLastButtons = true,
}) => {
  const totalPages = Math.ceil(resultCount / pageSize);

  const handlePageClick = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      onPageChange(newPage);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (onPageSizeChange) {
      // If parent provides onPageSizeChange, let parent handle page reset
      onPageSizeChange(newSize);
    } else {
      // Otherwise, just reset to first page
      onPageChange(0);
    }
  };

  const startItem = pageIndex * pageSize + 1;
  const endItem = Math.min((pageIndex + 1) * pageSize, resultCount);

  return (
    <div className="paginator">
      <div className="left-section">
        <div className="page-size-selector">
          <label htmlFor="pagination-page-size-select">Items per page:</label>
          <select
            id="pagination-page-size-select"
            value={pageSize}
            onChange={handlePageSizeChange}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="paginator-info">
        {startItem} - {endItem} of {resultCount.toLocaleString()}
      </div>

      <div className="right-section">
        <button
          onClick={() => handlePageClick(0)}
          disabled={pageIndex === 0}
          title="First page"
          className="first-page-btn"
        >
          &#171;
        </button>
        <button
          onClick={() => handlePageClick(pageIndex - 1)}
          disabled={pageIndex === 0}
          title="Previous page"
        >
          &#8249;
        </button>
        <button
          onClick={() => handlePageClick(pageIndex + 1)}
          disabled={pageIndex >= totalPages - 1}
          title="Next page"
        >
          &#8250;
        </button>
        <button
          onClick={() => handlePageClick(totalPages - 1)}
          disabled={pageIndex >= totalPages - 1}
          title="Last page"
          className="last-page-btn"
        >
          &#187;
        </button>
      </div>
    </div>
  );
};

export default PaginationComponent;
